import { lazy, ComponentType } from 'react';
import { tools, Tool } from '@/data/tools';
import { routeToPageName } from './stringUtils';
import { discoveredPages } from '@/data/discoveredToolsRegistry';

export interface GeneratedRoute {
    path: string;
    Component: ComponentType<any>;
    tool: Tool;
}

/**
 * Generate all tool routes dynamically from tools.ts
 * This is the single source of truth for routing
 */
export function generateRoutes(): GeneratedRoute[] {
    const routes: GeneratedRoute[] = [];

    for (const tool of tools) {
        try {
            const pageName = routeToPageName(tool.route);

            // Dynamically import the page component
            const Component = lazy(() => {
                // If it is an auto-discovered tool, load it from its registered dynamic import
                if (tool.id in discoveredPages) {
                    return discoveredPages[tool.id]();
                }

                // Handle nested/legacy paths (e.g., ai/TextGeneratorPage)
                if (pageName.includes('/')) {
                    const [folder, file] = pageName.split('/');
                    return import(`@/pages/${folder}/${file}.tsx`);
                }

                // Handle regular legacy paths (e.g., WordCounterPage)
                return import(`@/pages/${pageName}.tsx`);
            });

            routes.push({
                path: tool.route,
                Component,
                tool
            });
        } catch (error) {
            console.error(`Failed to generate route for ${tool.route}:`, error);
            // Continue processing other routes even if one fails
        }
    }

    return routes;
}

/**
 * Get route by tool ID
 */
export function getRouteByToolId(toolId: string): string | undefined {
    const tool = tools.find(t => t.id === toolId);
    return tool?.route;
}

/**
 * Get tool by route path
 */
export function getToolByRoute(route: string): Tool | undefined {
    return tools.find(t => t.route === route);
}

/**
 * Validate that all tools have corresponding page files
 * This can be used in tests or build scripts
 */
export function validateRoutes(): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    for (const tool of tools) {
        const pageName = routeToPageName(tool.route);

        // Auto-discovered tools do not need to follow legacy page naming rules
        if (tool.id in discoveredPages) {
            continue;
        }

        // Check naming convention for legacy tools
        if (!pageName.endsWith('Page')) {
            errors.push(`Tool "${tool.id}" has invalid page name: ${pageName} (must end with 'Page')`);
        }
    }

    return {
        valid: errors.length === 0,
        errors
    };
}
